import { useCallback, useEffect, useRef, type RefObject } from 'react'

/** Where focus goes once the list next changes. */
type Target = { kind: 'index'; index: number } | { kind: 'ids'; ids: ReadonlySet<string> } | null

/**
 * Focus follows a list that loses rows (shape §6.4): when a row leaves, focus
 * goes to the next row's primary control, else the previous one's, else
 * `fallback` — never to `<body>`.
 *
 * A caller says where it wants focus BEFORE the write (`request`), because the
 * row is still on screen then; the move happens when the ids next change, so a
 * failed write that removed nothing never spends it (the caller `cancel`s).
 * The effect is keyed on the ids as a string, not the array: a caller rebuilds
 * the array each render, which would spend the target on the first re-render.
 */
export function useFocusFollowsList(ids: readonly string[] | null, loading: boolean, fallback: RefObject<HTMLElement | null>) {
  const controls = useRef(new Map<string, HTMLElement>())
  const target = useRef<Target>(null)
  const key = ids ? ids.join('|') : null

  useEffect(() => {
    const wanted = target.current
    if (wanted === null || loading) return
    target.current = null
    const list = key ? key.split('|') : []
    const id = wanted.kind === 'ids' ? list.find((x) => wanted.ids.has(x)) : list[Math.min(wanted.index, list.length - 1)]
    const control = id ? controls.current.get(id) : undefined
    ;(control ?? fallback.current)?.focus()
  }, [key, loading, fallback])

  return {
    /** A ref callback for a row's primary control. */
    register: useCallback((id: string) => (el: HTMLElement | null) => {
      if (el) controls.current.set(id, el)
      else controls.current.delete(id)
    }, []),
    /** After the next change, focus the row now at `index`. */
    toIndex: useCallback((index: number) => void (target.current = { kind: 'index', index }), []),
    /** After the next change, focus the first remaining row among `failed`. */
    toFailed: useCallback((failed: ReadonlySet<string>) => void (target.current = { kind: 'ids', ids: failed }), []),
    cancel: useCallback(() => void (target.current = null), []),
  }
}
